import _, { capitalize } from "lodash";

import { NOT_EMPLOYED_STATUSES } from "shared/config/constants";
import { printDollarsFromCents } from "shared/utils/dollar-print";

import {
  ModalRecordItem,
  ModalSection,
  ModalSubItem,
} from "../components/ApplicantDetailModal";

import {
  getCreditHistoryData,
  getRentalHistoryData,
} from "./applicantHistoryHelpers";
import {
  deriveSentilinkView,
  getSentilinkEmptyCheckModalSubItems,
  getSentilinkModalDetailItems,
} from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";

import {
  getCreditApplicantSummary,
  getCriminalApplicantSummary,
  getIdentityApplicantSummary,
  getRentalApplicantSummary,
} from "./applicantStatusHelpers";

/**
 * Helper function to get the open balance for collections
 */
function getCollectionsOpenBalance(creditData: any): string {
  if (!creditData) {
    return null;
  }
  const openRecords = creditData.collections.records.filter(
    (record: any) => !record.date_closed
  );

  const totalOpenBalance = openRecords.reduce(
    (sum: number, record: any) => sum + (Number(record.currentBalance) || 0),
    0
  );

  return printDollarsFromCents(totalOpenBalance);
}

// Safely parse and format the verification type
const getFormattedVerificationType = (type: string): string => {
  try {
    if (!type || typeof type !== "string") return "N/A";
    const parts = type.split("/");
    if (parts.length < 2) return "N/A";

    const typePart = parts[1];
    if (!typePart) return "N/A";

    return typePart
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  } catch (error) {
    return "N/A";
  }
};

// Helper function to determine status for verification sub-items
const getVerificationSubItemStatus = (
  overallStatus: string,
  verificationStatus: string
): string | undefined => {
  return (overallStatus === "warn" || overallStatus === "error") &&
    verificationStatus !== "Passed"
    ? overallStatus
    : undefined;
};

const PERSONA_RUNS_AFTER_COPY =
  "Government ID & Selfie runs after the Identity Risk Check is cleared.";

/** Format a SentiLink `calledAt` ISO timestamp for the modal, or null if absent/invalid. */
const formatSentilinkCheckedAt = (calledAt: unknown): string | null => {
  if (typeof calledAt !== "string" || !calledAt) {
    return null;
  }
  const parsed = new Date(calledAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Build the "IDENTITY RISK CHECK" modal section (PRODUCT-1346): risk level,
 * reason(s), and the checked-at timestamp.
 */
const buildFraudRiskModalSection = (sentilink: any): ModalSection => {
  const view = deriveSentilinkView(sentilink);

  let riskLevelValue = "Not started";
  let riskLevelStatus: string | undefined;

  if (view?.isManuallyVerified) {
    riskLevelValue = "Manually verified";
    riskLevelStatus = "pass";
  } else if (view?.isError) {
    // An errored check is a soft warning (orange), not a hard failure, to match
    // the card chrome and the SentiLink verifications UX (PRODUCT-1282).
    riskLevelValue = "Error";
    riskLevelStatus = "warn";
  } else if (view?.triggered && view.tierKey) {
    riskLevelValue = view.isBlocked
      ? `${view.tierLabel} \u00b7 Blocked`
      : (view.tierLabel as string);
    if (view.isBlocked || view.tierKey === "HIGH_RISK") {
      riskLevelStatus = "fail";
    } else if (view.tierKey === "NEEDS_REVIEW") {
      riskLevelStatus = "warn";
    }
  } else if (view?.isBlocked) {
    // Blocked with no recognized tier: still surface the block so the modal does
    // not fall through to "Not started" and contradict the card's BLOCKED pill.
    riskLevelValue = "Blocked";
    riskLevelStatus = "fail";
  } else if (view?.notTriggered === "AWAITING") {
    riskLevelValue = "Awaiting re-submission";
  } else if (view?.notTriggered === "SKIPPED") {
    riskLevelValue = "Skipped (no SSN)";
  }

  const reasons = view?.isManuallyVerified ? [] : (view?.reasons ?? []);
  const checkedAt = formatSentilinkCheckedAt(sentilink?.calledAt);
  const manualVerification = view?.manualVerification;
  const verifiedAt = formatSentilinkCheckedAt(manualVerification?.verifiedAt);
  const reviewer =
    manualVerification?.reviewerName || manualVerification?.reviewerEmail;
  const emptySubItems =
    reasons.length === 0 && !view?.isManuallyVerified && !view?.isError
      ? getSentilinkEmptyCheckModalSubItems(view?.tierKey)
      : undefined;

  const items: ModalSection["items"] = [
    {
      label: "Risk Level",
      value: riskLevelValue,
      status: riskLevelStatus,
      subItems:
        reasons.length > 0
          ? reasons.map((description, idx) => {
              const sep = description.indexOf(": ");
              const checkLabel = sep > 0 ? description.slice(0, sep) : null;
              const value = sep > 0 ? description.slice(sep + 2) : description;
              return {
                label:
                  checkLabel ??
                  (reasons.length === 1 ? "Reason" : `Reason ${idx + 1}`),
                value,
                id: `sentilink-modal-reason-${idx}`,
              };
            })
          : emptySubItems,
    },
  ];

  if (checkedAt) {
    items.push({
      label: "Checked At",
      value: checkedAt,
    });
  }

  if (view?.isManuallyVerified) {
    if (reviewer) {
      items.push({
        label: "Verified By",
        value: reviewer,
      });
    }
    if (verifiedAt) {
      items.push({
        label: "Verified At",
        value: verifiedAt,
      });
    }
    if (manualVerification?.reviewedItemLabel) {
      items.push({
        label: "Reviewed Item",
        value: manualVerification.reviewedItemLabel,
      });
    }
    if (manualVerification?.details) {
      items.push({
        label: "Verification Details",
        value: manualVerification.details,
      });
    }
  }

  if (
    sentilink?.nameIdentityMismatch === true &&
    sentilink?.nameMismatchDetails &&
    !view?.isManuallyVerified
  ) {
    const details = sentilink.nameMismatchDetails as {
      idCredentialName?: string | null;
      submittedName?: string | null;
    };
    if (details.idCredentialName) {
      items.push({
        label: "ID credential name",
        value: details.idCredentialName,
        id: "sentilink-modal-name-mismatch-id",
        status: "fail",
      });
    }
    if (details.submittedName) {
      items.push({
        label: "Submitted name",
        value: details.submittedName,
        id: "sentilink-modal-name-mismatch-submitted",
        status: "fail",
      });
    }
  }

  return {
    title: "IDENTITY RISK CHECK",
    id: "idfraudrisk-modal-section",
    items,
  };
};

/**
 * Generate modal details for ID Verification.
 *
 * When SentiLink data exists the modal is split into two titled sections —
 * "IDENTITY RISK CHECK" and "PERSONA VERIFICATION" (PRODUCT-1346).
 * Non-SentiLink deals keep the original single (untitled) section.
 */
export const getIdVerificationModalDetails = (
  legacyApp: any,
  application: any,
  status: string
): ModalSection[] => {
  const { documentDetails } = getIdentityApplicantSummary(legacyApp);
  const documentCaptured =
    documentDetails !== "Pending" && documentDetails !== "Skipped";

  // Prepare sub-items for individual verifications
  const verificationSubItems = [];

  // Iterate through all idVerifications and create sub-items
  if (application?.idVerifications && application.idVerifications.length > 0) {
    application.idVerifications.forEach((idVerification, index) => {
      const verificationType = idVerification?.type;
      const verificationStatus =
        capitalize(idVerification?.attributes?.status) || "Unknown";
      const verificationTypeLabel =
        getFormattedVerificationType(verificationType);

      // Create descriptive message based on status
      const message = `${verificationTypeLabel} (${verificationStatus})`;

      verificationSubItems.push({
        label: `Verification ${index + 1}`,
        value: `${message}`,
        status: getVerificationSubItemStatus(status, verificationStatus),
      });
    });
  }

  const verificationStatusItem = {
    label: "Verification Status",
    value: legacyApp?.identity?.status || "Pending",
    status: status === "warn" || status === "error" ? status : undefined,
    subItems:
      verificationSubItems.length > 0 ? verificationSubItems : undefined,
  };

  const sentilink = legacyApp?.sentilink;

  // Non-SentiLink deals: keep the original single untitled section.
  if (!sentilink) {
    return [
      {
        items: [
          verificationStatusItem,
          {
            label: "Document Type",
            value: documentCaptured ? documentDetails : "N/A",
          },
          ...getSentilinkModalDetailItems(sentilink),
        ],
      },
    ];
  }

  const personaSection: ModalSection = {
    title: "PERSONA VERIFICATION",
    id: "persona-verification-modal-section",
    items: [
      verificationStatusItem,
      {
        label: "Government ID & Selfie",
        value: documentCaptured ? documentDetails : "Not captured yet",
      },
      {
        label: "Why",
        value: PERSONA_RUNS_AFTER_COPY,
      },
    ],
  };

  return [buildFraudRiskModalSection(sentilink), personaSection];
};

/**
 * Generate modal details for Income
 */
export const getIncomeModalDetails = (
  legacyApp: any,
  incomeDisplay: string,
  balanceDisplay: string,
  incomeStatus: string
): ModalSection[] => {
  // Get employment information
  const employments: any[] = _.get(legacyApp, "employmentHistories") || [];
  const employmentStatus = _.get(legacyApp, "employmentStatus");
  const skippedIncome = _.get(legacyApp, "skippedIncome", false);
  const income = _.get(legacyApp, "income.income", 0);
  const balance = _.get(legacyApp, "balance.balance", 0);

  // A record without an employer name is not listed and does not count as a
  // current employer, so it must be excluded before anything else is derived.
  const hasEmployerName = (emp: any) =>
    typeof emp.employerName === "string" && emp.employerName.trim().length > 0;

  const currentEmployments = employments.filter(
    (emp) => !emp.isPreviousEmployment && hasEmployerName(emp)
  );
  const linkedCurrentEmployments = currentEmployments.filter(
    (emp) => emp.isLinkedAutomatically
  );
  const linkedPreviousEmployments = employments.filter(
    (emp) => emp.isPreviousEmployment && emp.isLinkedAutomatically
  );

  let formattedEmployers: string;
  let employerSource: string | undefined;

  if (skippedIncome) {
    formattedEmployers = NOT_EMPLOYED_STATUSES.includes(employmentStatus)
      ? "The applicant stated that they have never been employed"
      : "The applicant skipped income verification";
  } else if (currentEmployments.length > 0) {
    formattedEmployers = currentEmployments
      .map((emp) => emp.employerName)
      .join(", ");
    // Source is only shown when self-reported data is part of the row.
    if (linkedCurrentEmployments.length === 0) {
      employerSource = "Manually entered";
    } else if (linkedCurrentEmployments.length < currentEmployments.length) {
      employerSource = "Linked and manually entered";
    }
  } else if (linkedPreviousEmployments.length > 0) {
    formattedEmployers = "The applicant did not link any current companies";
  } else {
    formattedEmployers = "Applicant did not link a company";
  }

  return [
    {
      items: [
        {
          label:
            income > 0 || incomeStatus === "pending"
              ? "Verified Income"
              : undefined,
          value: incomeDisplay,
          status:
            incomeStatus === "warn" || incomeStatus === "error"
              ? incomeStatus
              : undefined,
        },
        {
          label: balance != null ? "Cash Balance" : undefined,
          value: balanceDisplay,
          status:
            incomeStatus === "warn" || incomeStatus === "error"
              ? incomeStatus
              : undefined,
        },
        {
          label: "Employers",
          value: formattedEmployers,
          subItems: employerSource
            ? [{ label: "Source", value: employerSource }]
            : undefined,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Credit History
 */
export const getCreditHistoryModalDetails = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  isLowCreditScore: boolean,
  isThinCreditFile: boolean,
  creditScoreStatus: string,
  bankruptciesStatus: string,
  collectionsStatus: string,
  foreclosuresStatus: string
): ModalSection[] => {
  const { creditScore, bankruptcies, collections, foreclosures } =
    getCreditApplicantSummary(
      applicantLumenResult,
      application,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      isThinCreditFile,
      isLowCreditScore
    );

  const { creditScoreSummary } = application?.backgroundCreditCheck || {};

  // Get detailed credit history data if available
  const creditData = getCreditHistoryData(
    applicantLumenResult,
    creditScoreSummary
  );

  // Format bankruptcy records
  const bankruptcyRecords: ModalRecordItem[] = [];
  if (creditData?.bankruptcies?.records) {
    creditData.bankruptcies.records
      .filter((record: any) => record.date)
      .forEach((record: any) => {
        bankruptcyRecords.push({
          text: `Date: ${record.date}`,
        });
      });
  }

  // Format foreclosure records
  const foreclosureRecords: ModalRecordItem[] = [];
  if (creditData?.foreclosures?.records) {
    creditData.foreclosures.records.forEach((record: any) => {
      foreclosureRecords.push({
        text: `${record.state || "Unknown State"} - ${
          record.date || "Unknown Date"
        }`,
      });
    });
  }
  // Create collection detail items
  const collectionItems: ModalSubItem[] = [
    {
      label: "Total Open Balance",
      value: getCollectionsOpenBalance(creditData),
    },
  ];

  // Add credit collection records as a subitem with records
  if (collections === "Yes" && creditData?.collections?.records) {
    collectionItems.push({
      label: "Collection Records",
      value: creditData.collections.records.length,
    });
    creditData.collections.records.forEach((record: any) => {
      collectionItems.push({
        label: "Creditor",
        value: `${record.creditorsName} (${record.comments})`,
      });
    });
  }

  return [
    {
      items: [
        {
          label: "Credit Score",
          value: creditScore,
          status: creditScoreStatus,
        },
        {
          label: "Bankruptcies",
          value: bankruptcies,
          status: bankruptciesStatus,
          subItems:
            bankruptcies === "Yes"
              ? [
                  {
                    label: "Bankruptcy Records",
                    value: `${bankruptcyRecords.length} record${
                      bankruptcyRecords.length !== 1 ? "s" : ""
                    }`,
                    records: bankruptcyRecords,
                  },
                ]
              : undefined,
        },
        {
          label: "Collections",
          value: collections,
          status: collectionsStatus,
          subItems: collections === "Yes" ? collectionItems : undefined,
        },
        {
          label: "Foreclosures",
          value: foreclosures,
          status: foreclosuresStatus,
          subItems:
            foreclosures === "Yes"
              ? [
                  {
                    label: "Foreclosure Records",
                    value: `${foreclosureRecords.length} record${
                      foreclosureRecords.length !== 1 ? "s" : ""
                    }`,
                    records: foreclosureRecords,
                  },
                ]
              : undefined,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Rental History
 */
export const getRentalHistoryModalDetails = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  evictionsStatus: string,
  rentalCollectionsStatus: string
): ModalSection[] => {
  const { evictionRecords, rentalCollections } = getRentalApplicantSummary(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped
  );

  const { evictionRecords: applicantEvictionRecords = {} } =
    application?.backgroundCreditCheck || {};
  // Get detailed rental history data if available
  const rentalData = getRentalHistoryData(
    applicantLumenResult,
    applicantEvictionRecords?.evictionRecordGroup
  );

  // Create items for each eviction record
  const evictionRecordItems = [];

  if (rentalData?.evictionRecords?.records) {
    evictionRecordItems.push({
      label: "Eviction Records",
      value: rentalData?.evictionRecords?.records?.length,
    });
    // Process each eviction record as its own item with subitems
    rentalData.evictionRecords.records.forEach((record, index) => {
      // Add this eviction as a main item with subitems
      evictionRecordItems.push({
        label: `Record ${index + 1}`,
        value: `${record.state || "Unknown"} (${
          record.startDate || "Unknown"
        })`,
      });
    });
  }

  return [
    {
      items: [
        {
          label: "Eviction Records",
          value: evictionRecords,
          status: evictionsStatus,
          subItems: evictionRecords === "Yes" ? evictionRecordItems : undefined,
        },
        {
          label: "Rental Collections",
          value: rentalCollections,
          status: rentalCollectionsStatus,
          //   subItems:
          //     rentalCollections === "Yes" ? rentalCollectionItems : undefined,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Criminal History
 */
export const getCriminalHistoryModalDetails = (
  application: any,
  isBackgroundCreditCheckSkipped: boolean
): ModalSection[] => {
  const { criminalRecords } = getCriminalApplicantSummary(
    application,
    isBackgroundCreditCheckSkipped
  );
  const { backgroundCreditCheck = {} } = application || {};
  const { criminalRecords: applicantCriminalRecords } =
    backgroundCreditCheck || {};

  // Create items for each record group as its own section
  const recordGroupItems = [];

  // Check if we have records in the new format with nested records
  if (applicantCriminalRecords?.criminalRecordGroup) {
    recordGroupItems.push({
      label: "Criminal Records",
      value: applicantCriminalRecords.criminalRecordGroup.length,
    });
    // For each record group, create a separate item with sub-items
    applicantCriminalRecords.criminalRecordGroup.forEach(
      (recordGroup, index) => {
        // Create sub-items for this record group
        const recordSubItems: ModalRecordItem[] = [];

        // Add charges for this record group
        if (recordGroup.records && recordGroup.records.length > 0) {
          // If multiple records exist in this group, combine their charges
          if (recordGroup.records.length > 1) {
            const chargeTexts = recordGroup.records
              .map((record) => record.chargeText)
              .filter((charge) => !!charge)
              .join(", ");

            recordSubItems.push({
              text: `Charges: ${chargeTexts || "Unknown charges"}`,
            });
          } else {
            // Single record case
            const record = recordGroup.records[0];
            recordSubItems.push({
              text: `Charge: ${record.chargeText || "Unknown charge"}`,
            });
          }

          // Add severity for the first record
          const primaryRecord = recordGroup.records[0];
          if (primaryRecord.severity) {
            recordSubItems.push({
              text: `Severity: ${primaryRecord.severity}`,
            });
          }

          // Add outcome for the first record
          if (primaryRecord.outcome) {
            recordSubItems.push({
              text: `Outcome: ${primaryRecord.outcome}`,
            });
          }
        }

        // Add this record group as a main item with its details as sub-items
        recordGroupItems.push({
          label: `Record ${index + 1}`,
          value: `${recordGroup.state || "Unknown"} (${
            recordGroup.startDate || "Unknown Date"
          })`,
          records: recordSubItems,
        });
      }
    );
  }

  return [
    {
      items: [
        {
          label: "Criminal Records",
          value: criminalRecords,
          subItems: criminalRecords === "Yes" ? recordGroupItems : undefined,
        },
      ],
    },
  ];
};
